import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock } from "lucide-react";
import { PREVIEW_ALLOWLIST } from "@/config/featureFlags";
import { useToast } from "@/hooks/use-toast";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";


import { RecommendationsByFunction, type RawResponse } from "./RecommendationsByFunction";
import { ExecutiveNarrative, type FnScore as HeaderFnScore } from "@/reports/ExecutiveNarrative";
import { TrafficLightBars, type FnScore as TLBFnScore } from "@/components/report/TrafficLightBars";
import { FunctionRadar, type FnScore as RadarFnScore } from "@/components/report/FunctionRadar";
import { AssessmentSnapshot, type SnapshotScore } from "@/components/report/AssessmentSnapshot";
import { TopStrategicRisks, type RiskItem } from "@/components/report/TopStrategicRisks";
import { RemediationRoadmapSection, type RoadmapItem } from "@/components/report/RemediationRoadmapSection";
import { sentenceFor, type FnKey } from "@/reports/narratives";
import { getRecommendations, type FunctionKey, type MaturityLabel } from "@/lib/recommendationMap";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { resolveLogo, defaultLogo } from "@/lib/branding";
import {
  riskComparator,
  assignGroup,
  RECOMMENDATION_GROUPS,
  normalizeScore,
  priorityColor,
  priorityLabelFromWeight,
  calculatePriority,
  GROUP_COLORS,
  type RecommendationGroup,
} from "@/lib/riskOrdering";
import "@/reports/print.css";

type Fn2 = "GV" | "ID" | "PR" | "DE" | "RS" | "RC";

type QuestionRecommendations = Record<MaturityLabel, string[]>;

type QuestionResponse = {
  question_key: string;
  title: string;
  question_text: string;
  answer_label: MaturityLabel | "";
  score: number;
  nist_function?: string | null;
  control_codes?: string[];
};

const FN_LABELS: Record<Fn2, FnKey> = {
  GV: "Govern",
  ID: "Identify",
  PR: "Protect",
  DE: "Detect",
  RS: "Respond",
  RC: "Recover",
};

const isDev = import.meta.env.DEV;

// DB maturity_level -> display label mapping
const DB_LEVEL_TO_LABEL: Record<string, MaturityLabel> = {
  nonexistent: "Nonexistent",
  initial: "Initial",
  developing: "Developing",
  managed: "Defined/Managed",
  optimized: "Optimized",
};

const LABEL_TO_DB_LEVEL: Record<string, string> = {
  Nonexistent: "nonexistent",
  Initial: "initial",
  Developing: "developing",
  "Defined/Managed": "managed",
  Optimized: "optimized",
};

// (priority sort order now in src/lib/riskOrdering.ts)

// DB-driven recommendation row type
type DbRecRow = {
  question_id: number;
  maturity_level: string;
  intro_text: string | null;
  recommendations: string[];
  finding: string | null;
  notes: string | null;
  priority_weight: string;
};

// Indexed by question_id -> maturity_level (db key) -> DbRecRow
type DbRecMap = Record<number, Record<string, DbRecRow>>;

/**
 * Resolve NIST function from question_key prefix. Works for telecom (GV-xxx)
 * but NOT for AI (AI-S-xx). Always prefer DB-sourced nist_function over this.
 */
function functionFromQuestionKey(key: string): FunctionKey | null {
  if (!key) return null;
  const prefix = key.substring(0, 2).toUpperCase() as Fn2;
  return FN_LABELS[prefix] || null;
}

/**
 * Resolve NIST function for a question using DB map first, then prefix fallback.
 */
function resolveNistFunction(
  questionId: number,
  questionKey: string,
  nistMap: Record<number, string>
): FunctionKey | null {
  const fromDb = nistMap[questionId];
  if (fromDb) return fromDb as FunctionKey;
  return functionFromQuestionKey(questionKey);
}

function normalizeLabel(label: string): MaturityLabel | null {
  const validLabels: MaturityLabel[] = ["Nonexistent", "Initial", "Developing", "Defined/Managed", "Optimized"];
  if (validLabels.includes(label as MaturityLabel)) {
    return label as MaturityLabel;
  }
  return null;
}

// Ordering and badge helpers are centralized in riskOrdering.ts

type ScoreRow = { domain: string; avg_score: number; question_count: number; answered_count: number };

export default function SelfAssessmentPdf() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role: effectiveRole } = useEffectiveRole();
  const backPath = effectiveRole === "portfolio_manager" ? "/pm" : "/dashboard";
  const backLabel = effectiveRole === "portfolio_manager" ? "Back to Portfolio Dashboard" : "Back to Dashboard";
  const [rows, setRows] = useState<ScoreRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState<RawResponse[]>([]);
  const [responses, setResponses] = useState<QuestionResponse[]>([]);
  const [questionRecs, setQuestionRecs] = useState<Record<string, QuestionRecommendations>>({});
  const [hasAnyResponses, setHasAnyResponses] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState<string>("Draft");
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(defaultLogo);
  const [respondentEmail, setRespondentEmail] = useState<string | null>(null);
  const [viewerEmail, setViewerEmail] = useState<string>("");
  const [nistFunctionMap, setNistFunctionMap] = useState<Record<number, string>>({});
  const [dbRecMap, setDbRecMap] = useState<DbRecMap>({});
  // Map question_key -> question_id for DB lookups
  const [questionKeyToId, setQuestionKeyToId] = useState<Record<string, number>>({});
  const printRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Questionnaires whose recommendations live in question_recommendations_v2
  // (rich pipeline: finding / notes / intro_text / priority_weight per question × maturity).
  // All Cybersecurity Starter Assessments share this same renderer model.
  const DB_DRIVEN_REC_QUESTIONNAIRES = new Set<string>([
    "ai_llm_self_v1",
    "telecom_self_v1",
    "hrv_self_v1",
    "cyber_posture_v1",
    "data_privacy_law_v1",
    "iam_compromise_v1",
    "ir_ransomware_v1",
    "vendor_risk_lite_v1",
    "cyber_insurance_v1",
  ]);
  const isAiLlm = questionnaireId === "ai_llm_self_v1";
  const isTelecomSelf = questionnaireId === "telecom_self_v1";
  const isHrvSelf = questionnaireId === "hrv_self_v1";
  const isSelfAssessment = !!questionnaireId && DB_DRIVEN_REC_QUESTIONNAIRES.has(questionnaireId);
  // All self-assessments now use DB-driven recommendations (no legacy fallback)
  const useDbRecs = isSelfAssessment;
  const isSubmitted = assessmentStatus === "Submitted" || assessmentStatus === "In Review" || assessmentStatus === "Closed";

  // Allowlisted users can preview reports before submission
  const allowlistEmail = (viewerEmail || respondentEmail || "").trim().toLowerCase();
  const isAllowlisted = PREVIEW_ALLOWLIST.includes(allowlistEmail);

  // Block access to report if not submitted (for self-assessments) — unless allowlisted
  const shouldBlockReport = isSelfAssessment && !isSubmitted && !isAllowlisted;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // 1) scores by function
      const { data: scores, error: scoresErr } = await supabase.rpc(
        "respondent_get_scores_by_function",
        { p_assessment_id: assessmentId! }
      );

      if (scoresErr) {
        console.error("[PDF] respondent_get_scores_by_function error:", scoresErr);
      }

      console.log("[PDF] scores rows:", scores?.length ?? 0);

      // 2) get assessment metadata
      let orgNameLocal: string | null = null;
      let qId: string | null = null;
      let status = "Draft";
      let orgId: string | null = null;
      let submittedAtLocal: string | null = null;
      const { data: assessRpc, error: assessErr } = await (supabase.rpc as any)(
        'respondent_get_assessment',
        { p_assessment_id: assessmentId }
      );

      if (!assessErr && assessRpc && assessRpc.length > 0) {
        orgNameLocal = assessRpc[0].org_name ?? null;
        qId = assessRpc[0].questionnaire_id ?? null;
        status = assessRpc[0].status ?? "Draft";
        orgId = assessRpc[0].org_id ?? null;
        submittedAtLocal = assessRpc[0].submitted_at ?? null;
      }

      // 3) fetch responses + items + report data (controls+recs via RPC) + branding + respondent info + DB recommendations
      const [respResult, itemsResult, reportDataResult, brandingResult, assigneeResult, dbRecsResult, sessionResult] = await Promise.all([
        (supabase.rpc as any)('respondent_get_responses', { p_assessment_id: assessmentId }),
        qId
          ? (supabase.rpc as any)('respondent_get_questionnaire_items', {
              p_assessment_id: assessmentId,
              p_questionnaire_id: qId,
            })
          : Promise.resolve({ data: [], error: null }),
        (supabase.rpc as any)('get_self_assessment_report_data', { p_assessment_id: assessmentId }),
        orgId
          ? supabase.rpc("get_assessment_org_logo" as any, { p_org_id: orgId })
          : Promise.resolve({ data: null, error: null }),
        assessmentId
          ? supabase.from("assessment_assignees").select("user_id").eq("assessment_id", assessmentId).eq("role", "respondent").limit(1)
          : Promise.resolve({ data: null, error: null }),
        // Fetch DB-driven recommendations via RPC
        (() => {
          console.log("[PDF] Fetching recommendations for questionnaire:", qId);
          return qId
            ? (supabase.rpc as any)('get_question_recommendations_v2', { p_questionnaire_key: qId })
            : Promise.resolve({ data: [], error: null });
        })(),
        supabase.auth.getSession(),
      ]);

      const viewerEmailLocal = sessionResult.data.session?.user?.email?.trim().toLowerCase() ?? "";

      const responseData = respResult.data ?? [];
      const respErr = respResult.error;
      const itemsData = itemsResult.data ?? [];

      // Build nist function map and control codes map from RPC result
      const nistMap: Record<number, string> = {};
      const controlCodesMap: Record<number, string[]> = {};
      const reportData = reportDataResult.data;
      if (reportDataResult.error) {
        console.error("[PDF] get_self_assessment_report_data RPC FAILED:", reportDataResult.error);
      } else if (reportData) {
        const controls = Array.isArray(reportData.controls) ? reportData.controls : [];
        console.log("[PDF] RPC controls rows:", controls.length);
        for (const row of controls) {
          if (row.question_id && row.nist_function && !nistMap[row.question_id]) {
            nistMap[row.question_id] = row.nist_function;
          }
          if (row.question_id && row.code) {
            if (!controlCodesMap[row.question_id]) controlCodesMap[row.question_id] = [];
            controlCodesMap[row.question_id].push(row.code);
          }
        }
      }
      console.log("[PDF] nistMap entries:", Object.keys(nistMap).length);
      console.log("[PDF] controlCodesMap entries:", Object.keys(controlCodesMap).length);

      // Build DB recommendation map
      const recMapDb: DbRecMap = {};
      console.log("[PDF] Recommendations RPC returned:", dbRecsResult.data?.length ?? 0, "rows, error:", dbRecsResult.error);
      if (dbRecsResult.error) {
        console.error("[PDF] get_question_recommendations_v2 RPC FAILED:", dbRecsResult.error);
      } else if (dbRecsResult.data && Array.isArray(dbRecsResult.data)) {
        for (const row of dbRecsResult.data) {
          if (!recMapDb[row.question_id]) recMapDb[row.question_id] = {};
          recMapDb[row.question_id][row.maturity_level] = {
            question_id: row.question_id,
            maturity_level: row.maturity_level,
            intro_text: row.intro_text,
            recommendations: Array.isArray(row.recommendations) ? row.recommendations : [],
            finding: row.finding,
            notes: row.notes,
            priority_weight: row.priority_weight,
          };
        }
        console.log("[PDF] DB recommendations loaded:", Object.keys(recMapDb).length, "questions,", dbRecsResult.data.length, "total rows");
        if (dbRecsResult.data.length > 0) {
          console.log("[PDF] Sample DB rec row:", JSON.stringify(dbRecsResult.data[0]));
        }
      }

      // Resolve logo
      let resolvedLogo = defaultLogo;
      const brandingRow = Array.isArray(brandingResult?.data) ? brandingResult.data[0] : brandingResult?.data;
      if (brandingRow?.logo_url) {
        resolvedLogo = resolveLogo({ companyLogoUrl: brandingRow.logo_url });
      }

      // Resolve respondent email
      let respondentEmailLocal: string | null = null;
      if (assigneeResult?.data && assigneeResult.data.length > 0) {
        const userId = assigneeResult.data[0].user_id;
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("id", userId)
          .maybeSingle();
        if (profile?.display_name) {
          respondentEmailLocal = profile.display_name;
        }
      }
      if (!respondentEmailLocal && viewerEmailLocal) {
        respondentEmailLocal = viewerEmailLocal;
      }

      console.log("[PDF] responses rows:", responseData.length, "items rows:", itemsData.length);

      const questionMap = new Map<number, { question_key: string; title: string; question_text: string; question_id: number }>();
      const keyToIdLocal: Record<string, number> = {};
      for (const item of itemsData) {
        questionMap.set(item.question_id, {
          question_key: item.question_key,
          title: item.title ?? '',
          question_text: item.question_text ?? '',
          question_id: item.question_id,
        });
        keyToIdLocal[item.question_key] = item.question_id;
      }

      const questionResponses: QuestionResponse[] = [];
      let rawForFunctionPage: RawResponse[] = [];

      if (!respErr && responseData) {
        responseData.forEach((r: any) => {
          const qMeta = questionMap.get(r.question_id);
          if (!qMeta) return;

          const numericScore = normalizeScore(r.score, 50);
          if (!Number.isFinite(Number(r.score))) {
            console.log("[PDF] normalized invalid score", {
              question_id: r.question_id,
              rawScore: r.score,
              normalizedScore: numericScore,
            });
          }

          let label: MaturityLabel | "" = "";
          if (numericScore === 0) label = "Nonexistent";
          if (numericScore === 25) label = "Initial";
          if (numericScore === 50) label = "Developing";
          if (numericScore === 75) label = "Defined/Managed";
          if (numericScore === 100) label = "Optimized";

          // Use resolveNistFunction for robust DB-first resolution
          const nistFn = resolveNistFunction(r.question_id, qMeta.question_key, nistMap);
          const codes = controlCodesMap[r.question_id] || [];

          const q = {
            question_key: qMeta.question_key || "",
            title: qMeta.title || "",
            question_text: qMeta.question_text || "",
            answer_label: label || "",
            score: numericScore,
            nist_function: nistFn,
            control_codes: codes,
          };

          if (q.question_key && q.answer_label) {
            questionResponses.push(q as QuestionResponse);

            rawForFunctionPage.push({
              question_key: q.question_key,
              title:        q.title,
              question_text: q.question_text,
              answer_label:  q.answer_label,
              nist_function: nistFn,
              control_codes: codes,
            } as RawResponse);
          }
        });
      }

      // 4) question recommendations from RPC result (legacy fallback for questionnaires without DB recs)
      let recMap: Record<string, QuestionRecommendations> = {};
      const hasDbRecs = Object.keys(recMapDb).length > 0;
      const isDbDrivenQuestionnaire = !!qId && DB_DRIVEN_REC_QUESTIONNAIRES.has(qId);
      console.log("[PDF] questionnaireId resolved:", qId, "| isDbDrivenQuestionnaire:", isDbDrivenQuestionnaire, "| hasDbRecs:", hasDbRecs);
      if (!isDbDrivenQuestionnaire && reportData && Array.isArray(reportData.recommendations)) {
        for (const row of reportData.recommendations) {
          if (!row.question_key || !row.recommendations_json) continue;
          recMap[row.question_key] = row.recommendations_json as QuestionRecommendations;
        }
        console.log("[PDF] legacy recommendations keys from RPC:", Object.keys(recMap).length);
      }

      // For DB-driven questionnaires: validate DB rec coverage is complete
      if (isDbDrivenQuestionnaire && Object.keys(recMapDb).length === 0) {
        console.error(`[PDF] FATAL: No DB recommendations found for ${qId}. Report will show errors.`);
      }

      // Always log pipeline summary for production diagnostics
      console.log("[PDF] === DATA PIPELINE SUMMARY ===");
      console.log("[PDF] questionnaireId:", qId);
      console.log("[PDF] isDbDrivenQuestionnaire:", isDbDrivenQuestionnaire);
      console.log("[PDF] questionResponses count:", questionResponses.length);
      console.log("[PDF] DB rec map questions:", Object.keys(recMapDb).length);
      console.log("[PDF] legacy questionRecs keys:", Object.keys(recMap).length);
      console.log("[PDF] Using fallback:", !isDbDrivenQuestionnaire && Object.keys(recMapDb).length === 0);
      const withFn = questionResponses.filter(r => r.nist_function);
      const withCodes = questionResponses.filter(r => r.control_codes && r.control_codes.length > 0);
      console.log("[PDF] responses WITH nist_function:", withFn.length, "/", questionResponses.length);
      console.log("[PDF] responses WITH control_codes:", withCodes.length, "/", questionResponses.length);
      if (questionResponses.length > 0) {
        console.log("[PDF] sample response:", JSON.stringify(questionResponses[0]));
      }
      // Log key-to-id mapping for cross-referencing DB recs
      console.log("[PDF] questionKeyToId sample:", JSON.stringify(Object.entries(keyToIdLocal).slice(0, 3)));
      console.log("[PDF] === END SUMMARY ===");

      if (!cancelled) {
        setRows((scores as ScoreRow[]) || []);
        setOrgName(orgNameLocal);
        setResponses(questionResponses);
        setQuestionnaireResponses(rawForFunctionPage);
        setQuestionRecs(recMap);
        setHasAnyResponses(responseData.length > 0);
        setAssessmentStatus(status);
        setQuestionnaireId(qId);
        setSubmittedAt(submittedAtLocal);
        setLogoUrl(resolvedLogo);
        setRespondentEmail(respondentEmailLocal);
        setViewerEmail(viewerEmailLocal);
        setNistFunctionMap(nistMap);
        setDbRecMap(recMapDb);
        setQuestionKeyToId(keyToIdLocal);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const scores = rows || [];

  const radarScores: RadarFnScore[] = useMemo(() => {
    if (!scores?.length) return [];
    return scores.map((s) => ({
      fn: FN_LABELS[s.domain.toUpperCase() as Fn2] as FnKey,
      value: Math.round(s.avg_score ?? 0),
    }));
  }, [scores]);

  const tlbScores: TLBFnScore[] = useMemo(() => {
    if (!scores?.length) return [];
    return scores.map((s) => ({
      fn: FN_LABELS[s.domain.toUpperCase() as Fn2] as TLBFnScore["fn"],
      value: Math.round(s.avg_score ?? 0),
    }));
  }, [scores]);

  const narratives: Partial<Record<FnKey, string>> = useMemo(() => {
    return tlbScores.reduce(
      (acc, s) => {
        acc[s.fn] = sentenceFor(s.fn, s.value, questionnaireId);
        return acc;
      },
      {} as Partial<Record<FnKey, string>>,
    );
  }, [tlbScores, questionnaireId]);

  const fnScores: HeaderFnScore[] = useMemo(() => {
    return radarScores.map((s) => ({
      fn: s.fn as HeaderFnScore["fn"],
      you: s.value,
    }));
  }, [radarScores]);

  const snapshotScores: SnapshotScore[] = useMemo(() => {
    return radarScores.map((s) => ({ fn: s.fn, value: s.value }));
  }, [radarScores]);

  const totalControls = useMemo(() => {
    return scores.reduce((sum, s) => sum + (s.answered_count ?? 0), 0);
  }, [scores]);

  const overallProgress = useMemo(() => {
    const totalQ = scores.reduce((sum, s) => sum + (s.question_count ?? 0), 0);
    const answeredQ = scores.reduce((sum, s) => sum + (s.answered_count ?? 0), 0);
    return totalQ > 0 ? Math.round((answeredQ / totalQ) * 100) : 0;
  }, [scores]);

  /** Inherent priority weight from DB (source of truth) */
  const getInherentPriorityWeight = (questionKey: string): string => {
    const qId = questionKeyToId[questionKey];
    if (!qId || !dbRecMap[qId]) {
      if (isAiLlm) {
        console.error(`[PDF] FATAL: No DB priority weight for AI question ${questionKey}. No fallback permitted.`);
      }
      return "medium";
    }

    const levels = Object.values(dbRecMap[qId]);
    return levels[0]?.priority_weight ?? "medium";
  };

  /** Residual priority (display priority) = inherent weight + maturity effectiveness */
  const getResidualPriority = (
    questionKey: string,
    maturityLabel: string | null | undefined,
  ): keyof typeof import("@/lib/riskOrdering").PRIORITY_ORDER => {
    const inherentWeight = getInherentPriorityWeight(questionKey);
    const isTrace = questionKey === "AI-S-05";
    if (isTrace) {
      console.log(`[TRACE getResidualPriority] key=${questionKey} | inherentWeight="${inherentWeight}" | maturityLabel="${maturityLabel}"`);
    }
    const result = calculatePriority(inherentWeight, maturityLabel, isTrace ? questionKey : undefined);
    if (isTrace) {
      console.log(`[TRACE getResidualPriority] key=${questionKey} | calculatePriority result="${result}" | badge="${priorityLabelFromWeight(result)}"`);
    }
    return result;
  };

  // Top strategic risks: sort by residual priority (inherent + maturity), then score
  const topRisks: RiskItem[] = useMemo(() => {
    if (!responses.length) return [];
    return [...responses]
      .filter((r) => r.score <= 50 && r.answer_label)
      .map((r, idx) => {
        const pw = getInherentPriorityWeight(r.question_key);
        if (r.question_key === "AI-S-05") {
          console.log(`[TRACE topRisks] key=${r.question_key} | title="${r.title}" | score=${r.score} | answer_label="${r.answer_label}" | inherentWeight="${pw}" | residual="${calculatePriority(pw, r.answer_label, r.question_key)}"`);
        }
        return {
          title: r.title,
          nistFunction: r.nist_function as FunctionKey | null,
          score: r.score,
          maturity: r.answer_label,
          priorityWeight: pw,
          questionIndex: idx,
        };
      })
      .sort((a, b) =>
        riskComparator(
          {
            priorityWeight: calculatePriority(a.priorityWeight, a.maturity || null),
            score: a.score,
            questionIndex: a.questionIndex,
          },
          {
            priorityWeight: calculatePriority(b.priorityWeight, b.maturity || null),
            score: b.score,
            questionIndex: b.questionIndex,
          },
        )
      )
      .slice(0, 5);
  }, [responses, dbRecMap, questionKeyToId]);

  const roadmapItems: RoadmapItem[] = useMemo(() => {
    if (!responses.length) return [];
    return [...responses]
      .filter((r) => normalizeScore(r.score, 50) <= 75 && r.answer_label)
      .map((r, idx) => {
        const residualPriority = getResidualPriority(r.question_key, r.answer_label);
        const numericScore = normalizeScore(r.score, 50);

        return {
          title: r.title,
          nistFunction: (r.nist_function as string) || "—",
          priority: priorityLabelFromWeight(residualPriority),
          priorityWeight: getInherentPriorityWeight(r.question_key),
          score: numericScore,
          questionIndex: idx,
          maturityLabel: normalizeLabel(r.answer_label),
        };
      });
  }, [responses, dbRecMap, questionKeyToId]);

  const assessmentDateStr = useMemo(() => {
    if (submittedAt) {
      const d = new Date(submittedAt);
      return d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
    }
    const d = new Date();
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  }, [submittedAt]);

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }, []);

  const assessmentTitle = useMemo(() => {
    return getQuestionnaireDisplayTitle(questionnaireId || "");
  }, [questionnaireId]);

  if (loading) {
    return (
      <div className="p-6 print-container">
        <h2 className="text-xl font-semibold">Preparing Self-Assessment Report…</h2>
      </div>
    );
  }

  if (!hasAnyResponses) {
    return (
      <div className="p-6 print-container max-w-lg mx-auto mt-20 text-center space-y-4">
        <h2 className="text-xl font-semibold">
          {isAllowlisted ? "Preview Not Available" : "No data to export"}
        </h2>
        <p className="text-muted-foreground">
          {isAllowlisted
            ? "Please select at least one answer to generate a report preview."
            : "Answer at least one question in the assessment, then try again."}
        </p>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    );
  }

  // Block report access for self-assessments that haven't been submitted
  if (shouldBlockReport) {
    return (
      <div className="p-6 print-container max-w-lg mx-auto mt-20 text-center space-y-4">
        <Lock className="h-12 w-12 text-muted-foreground mx-auto" />
        <h2 className="text-xl font-semibold">Report Not Available</h2>
        <p className="text-muted-foreground">
          This report will be generated after you submit your assessment. Please complete and submit all questions first.
        </p>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="print-root" ref={rootRef}>
      <div className="print-sentinel" aria-hidden="true" />

      {/* PAGE 1: Header + Snapshot + Executive Summary + Traffic Light Bars + Radar */}
      <div className="print-page-one p-6" ref={printRef}>
        {/* Wrap header + executive summary to keep them on the same page */}
        <div className="report-first-page">
          {/* Assessment Header */}
          <div className="pdf-header border-b border-border pb-1">
            <div className="flex items-start gap-2">
              <img
                src={logoUrl}
                alt={orgName || "Organization logo"}
                className="h-9 w-auto object-contain flex-shrink-0 print-logo block leading-none"
                style={{ maxWidth: '64px', maxHeight: '36px' }}
                onError={(e) => { (e.target as HTMLImageElement).src = defaultLogo; }}
              />
              <div className="flex-1 min-w-0">
                <h1 className="text-base font-bold text-foreground leading-tight">{assessmentTitle}</h1>
                <div className="mt-0.5 grid grid-cols-2 gap-x-4 gap-y-0 text-xs text-muted-foreground">
                  <div><span className="font-medium text-foreground">Organization:</span> {orgName || "—"}</div>
                  <div><span className="font-medium text-foreground">Assessment Date:</span> {assessmentDateStr}</div>
                  {respondentEmail && (
                    <div><span className="font-medium text-foreground">Assigned To:</span> {respondentEmail}</div>
                  )}
                  <div><span className="font-medium text-foreground">Status:</span> {assessmentStatus}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 self-end flex-shrink-0">
                <span className="text-[10px] text-muted-foreground italic">Powered by</span>
                <span className="text-sm font-bold italic text-[hsl(217,91%,60%)]">CSVPro</span>
              </div>
            </div>
          </div>

          {/* Assessment Snapshot */}
          <div className="mt-1.5">
            <AssessmentSnapshot scores={snapshotScores} totalControls={totalControls} />
          </div>

          {/* Executive Summary */}
          <div className="executive-summary mt-1.5">
            <ExecutiveNarrative orgName={orgName || "Organization"} dateStr={todayStr} functionScores={fnScores} questionnaireId={questionnaireId} />
          </div>
        </div>

        <div className="report-block mt-4">
          <TrafficLightBars scores={tlbScores} narratives={narratives} />
        </div>

        <div className="report-block mt-4">
          <FunctionRadar scores={radarScores} orgName={orgName || "Organization"} />
        </div>
      </div>

      {/* PAGE 2+: Top Strategic Risks + Recommendations */}
      {responses && responses.length > 0 && (
        <div className="print-page-two p-6">
          {/* Top Strategic Risks */}
          {topRisks.length > 0 && (
            <div className="mb-6">
              <TopStrategicRisks risks={topRisks} />
            </div>
          )}

          {/* Recommendations & Notes — grouped by priority band */}
          <h2 className="text-lg font-bold mb-4">Recommendations & Notes</h2>
          {(() => {
            const normalizedItems = responses.map((r, idx) => {
              const residualPriority = getResidualPriority(r.question_key, r.answer_label);
              const score = normalizeScore(r.score, 50);
              const questionIndex = idx;

              return {
                ...r,
                priority_weight: residualPriority,
                score,
                questionIndex,
                priorityBadge: priorityLabelFromWeight(residualPriority) as "Critical" | "High" | "Medium" | "Low",
              };
            });

            const orderedItems = [...normalizedItems].sort((a, b) =>
              riskComparator(
                { priorityWeight: a.priority_weight, score: a.score, questionIndex: a.questionIndex },
                { priorityWeight: b.priority_weight, score: b.score, questionIndex: b.questionIndex },
              )
            );

            console.log("[Final ordered items]", orderedItems.map((i) => ({
              title: i.title,
              priority_weight: i.priority_weight,
              score: Number(i.score),
              questionIndex: i.questionIndex,
            })));

            const groups = new Map<RecommendationGroup, typeof orderedItems>();
            orderedItems.forEach((item) => {
              const label = normalizeLabel(item.answer_label);
              const group = assignGroup(item.priority_weight, label);
              if (!groups.has(group)) groups.set(group, []);
              groups.get(group)!.push(item);
            });

            const groupedOrder = RECOMMENDATION_GROUPS.flatMap((groupName) => groups.get(groupName) ?? []);
            console.log("[Final ordered items]", groupedOrder.map((i) => ({
              title: i.title,
              priority_weight: i.priority_weight,
              score: Number(i.score),
              questionIndex: i.questionIndex,
            })));

            const renderOrder = RECOMMENDATION_GROUPS.flatMap((groupName) => groups.get(groupName) ?? []);
            console.log("[Final ordered items]", renderOrder.map((i) => ({
              title: i.title,
              priority_weight: i.priority_weight,
              score: Number(i.score),
              questionIndex: i.questionIndex,
            })));

            return RECOMMENDATION_GROUPS.map((groupName) => {
              const items = groups.get(groupName);
              if (!items || items.length === 0) return null;

              return (
                <div key={groupName} className={`mb-6 priority-group${groupName === "Medium Priority Improvements" || groupName === "Maintain & Optimize" ? " priority-group-break" : ""}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide pb-1 mb-3 border-b priority-group-header ${GROUP_COLORS[groupName].text} ${GROUP_COLORS[groupName].border}`}>
                    {groupName}
                  </h3>
                  <div className={`space-y-4 rounded-lg border priority-group-cards ${GROUP_COLORS[groupName].border} p-3`}>
                    {items.map((r) => {
                      const fn = r.nist_function as FunctionKey | null;
                      const label = normalizeLabel(r.answer_label);
                      const priority = r.priorityBadge;
                      if (r.question_key === "AI-S-05") {
                        console.log(`[TRACE RENDER] key=${r.question_key} | answer_label="${r.answer_label}" | priority_weight="${r.priority_weight}" | priorityBadge="${priority}" | score=${r.score}`);
                      }
                      const isOptimized = label === "Optimized";

                      let bullets: string[] = [];
                      let finding = "";
                      let recommendation = "";
                      let notes = "";
                      let introText = "";

                      // 1) Try DB-driven recommendations (v2) — primary source
                      const qId = questionKeyToId[r.question_key];
                      const dbLevel = label ? LABEL_TO_DB_LEVEL[label] : null;
                      if (qId && dbLevel && dbRecMap[qId]?.[dbLevel]) {
                        const dbRec = dbRecMap[qId][dbLevel];
                        finding = dbRec.finding || "";
                        recommendation = Array.isArray(dbRec.recommendations)
                          ? dbRec.recommendations.join(" ")
                          : "";
                        notes = dbRec.notes || "";
                        introText = dbRec.intro_text || "";
                      } else if (isAiLlm) {
                        console.error(`[PDF] FATAL: Missing DB recommendation for AI question_id=${qId} (${r.question_key}), level=${dbLevel}. No fallback permitted.`);
                        finding = `⚠ ERROR: Missing recommendation data for ${r.question_key} at maturity level "${label}". Please contact your administrator to seed the question_recommendations_v2 table.`;
                      } else if (qId && dbLevel) {
                        console.warn(`[PDF] Missing DB recommendation for question_id=${qId} (${r.question_key}), level=${dbLevel}. Falling back.`);
                      }

                      // 2) Fallback: legacy DB-sourced questionRecs (for non-AI questionnaires ONLY)
                      if (!isAiLlm && !finding && !recommendation) {
                        const perQuestion = label && questionRecs[r.question_key];
                        if (perQuestion && label) {
                          const recData = perQuestion[label];
                          if (Array.isArray(recData)) {
                            bullets = recData;
                          } else if (recData && typeof recData === "object") {
                            const structured = recData as any;
                            finding = structured.finding || "";
                            recommendation = structured.recommendation || "";
                            notes = structured.notes || "";
                          }
                        }
                      }

                      // 3) Function-level fallback (last resort for non-AI questionnaires ONLY)
                      if (!isAiLlm && (!bullets || bullets.length === 0) && !finding && fn && label) {
                        bullets = getRecommendations(fn, label);
                      }

                      // Cap bullets for Optimized to 2 max
                      if (isOptimized && bullets.length > 2) {
                        bullets = bullets.slice(0, 2);
                      } else if (!isOptimized && bullets.length > 4) {
                        bullets = bullets.slice(0, 4);
                      }

                      return (
                        <div key={r.questionIndex} className="border rounded-lg p-4 rec-card">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-sm font-semibold leading-tight">{r.title}</h3>
                            <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${priorityColor(priority)}`}>
                              {priority}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mb-2">
                            {fn && <span>Function: <span className="font-medium text-foreground">{fn}</span></span>}
                            {label && <span>Maturity: <span className="font-medium text-foreground">{label}</span></span>}
                            {r.control_codes && r.control_codes.length > 0 && (
                              <span>Controls: <span className="font-medium text-foreground">{r.control_codes.join(" · ")}</span></span>
                            )}
                          </div>

                          {introText && (
                            <p className="text-xs text-muted-foreground italic mb-2">{introText}</p>
                          )}

                          {finding && (
                            <div className="mb-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Finding</div>
                              <p className="text-sm">{finding}</p>
                            </div>
                          )}
                          {recommendation && (
                            <div className="mb-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Recommendation</div>
                              <p className="text-sm">{recommendation}</p>
                            </div>
                          )}
                          {notes && (
                            <div className="mb-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Notes</div>
                              <p className="text-sm">{notes}</p>
                            </div>
                          )}

                          {!finding && bullets && bullets.length > 0 && (
                            <>
                              <p className="text-xs text-muted-foreground mb-1">{r.question_text}</p>
                              <ul className="list-disc list-inside space-y-0.5 text-sm">
                                {bullets.map((b, bidx) => (
                                  <li key={bidx}>{b}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}

          {/* Remediation Roadmap */}
          {roadmapItems.length > 0 && (
            <div className="mt-6 remediation-section">
              <RemediationRoadmapSection items={roadmapItems} />
            </div>
          )}
        </div>
      )}

      {/* Print footer – visible only when printing */}
      <div className="print-footer" aria-hidden="true">
        <span className="italic">Powered by </span>
        <span className="font-bold italic" style={{ color: 'hsl(217, 91%, 60%)' }}>CSVPro</span>
      </div>

      {/* Action buttons (hidden in print) */}
      <div className="no-print p-6 pt-4 space-y-4">
        {isSelfAssessment && isSubmitted && (
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-950/30">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-green-700 dark:text-green-300" />
              <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                This assessment has been finalized and cannot be edited.
              </p>
            </div>
          </div>
        )}

        {isSelfAssessment && !isSubmitted && isAllowlisted && (
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
              {overallProgress >= 100
                ? "🔍 QA Preview Mode — This report reflects a fully completed assessment"
                : "🔍 QA Preview Mode — This report is generated from an assessment that may be partially completed"}
            </p>
          </div>
        )}

        {shouldBlockReport && (
          <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50 dark:bg-amber-950/30">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
              This report is available after submission. Please complete and submit your assessment first.
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate(backPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backLabel}
          </Button>
          {(isSubmitted || isAllowlisted) && (
            <Button
              variant="outline"
              onClick={() => {
                setTimeout(() => window.print(), 500);
              }}
            >
              Print
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
